import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { api } from '../services/api'

interface User {
  email: string
  fullName: string
}

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  signIn: (email: string, password: string) => Promise<boolean>
  signUp: (email: string, password: string, fullName: string) => Promise<boolean>
  signOut: () => void
  clearError: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (api.hasTokens()) {
        const isValid = await api.verifyToken()
        if (isValid) {
          // In a real app, you'd fetch user profile here
          // For now, we'll get the email from localStorage or leave it null
          const storedEmail = localStorage.getItem('user_email')
          const storedName = localStorage.getItem('user_name')
          if (storedEmail) {
            setUser({ email: storedEmail, fullName: storedName || '' })
          }
        } else {
          api.clearTokens()
        }
      }
      setIsLoading(false)
    }
    checkAuth()
  }, [])

  const signIn = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await api.obtainToken(email, password)
      // Store user info (in production, fetch from /api/me endpoint)
      localStorage.setItem('user_email', email)
      setUser({ email, fullName: '' })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signUp = useCallback(async (email: string, password: string, fullName: string): Promise<boolean> => {
    setIsLoading(true)
    setError(null)

    try {
      await api.register(email, password, fullName)
      localStorage.setItem('user_email', email)
      localStorage.setItem('user_name', fullName)
      setUser({ email, fullName })
      return true
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create account'
      setError(message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [])

  const signOut = useCallback(() => {
    api.clearTokens()
    localStorage.removeItem('user_email')
    localStorage.removeItem('user_name')
    setUser(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    signIn,
    signUp,
    signOut,
    clearError,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

